// Copyright 2020 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { Router } from "aurelia-router";
import { TimelineEntryDTO } from "datatypes/SoramaWebApi";
import { fixedEncodeURIComponent } from "extensions/UriExtensions";
import { WorkflowModuleTimelineEvent } from "libraries/WorkflowFramework/WorkflowModule/WorkflowModuleTimeEvent";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { TimelineEntriesRepository } from "repositories/TimelineEntriesRepository";
import { ExportService } from "services/Export/ExportService";

/**
 * Handles creation, viewing, and opening/restoring modules of entries in the Timeline.
 * Timeline entries are created when the user makes changes to analysis modules in the analysis workflow.
 * Timeline entries are highlighted when the user clicks the "Bookmark" button in any of the analysis modules.
 * Timeline entries can be viewed in the "Timeline" component, and from there, can be opened/restored.
 */
@autoinject
export class TimelineService {
    public measurementNameMap: { [measurementId: string]: string } = { Unknown: "Unknown Measurement" };

    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly timelineEventRepository: TimelineEntriesRepository,
        private readonly router: Router,
        private readonly exportService: ExportService,
        private readonly shellviewManager: ShellViewManager
    ) {}

    //#region Public methods
    // Restores the state of the module that created the timeline entry, opens the analysis workflow if needed
    public open(entry: TimelineEntryDTO): void {
        const timelineEvent = new WorkflowModuleTimelineEvent(entry, this.timelineEventRepository, this.exportService);
        const workflow = this.shellviewManager.activeWorkflow;
        if (workflow != undefined && this.shellviewManager.activeWorkflow.workflowName == "Analysis") {
            workflow.addModule(timelineEvent.getWorkflowModuleConfig()).then(async (newModule) => {
                await newModule.moduleInstance.restoreModuleState(timelineEvent, false);
                // Add the frequency ranges to the query string so that the cached analysis workflow is overwritten with the bookmarked frequency ranges
                this.router.navigate(window.location.pathname + this.generateFrequencyRangesQueryString(entry), {
                    trigger: true,
                    replace: true,
                });
            });
        } else {
            // Navigate to analysis workflow
            // We add the frequency ranges to the query string so that the cached analysis workflow is overwritten with the bookmarked frequency ranges
            const sidepanel =
                this.shellviewManager.rightSidebarPanelName != undefined
                    ? "&[sidebar]name=" + this.shellviewManager.rightSidebarPanelName
                    : "";
            this.router.navigate(
                "/Analysis?[timeline]entryId=" + entry.id + sidepanel + this.generateFrequencyRangesQueryString(entry),
                { trigger: true, replace: true }
            );
        }
    }

    public getMeasurementId(entry: TimelineEntryDTO): string {
        if (entry.properties.selectedMeasurementIds && entry.properties.selectedMeasurementIds.length > 0) {
            return entry.properties.selectedMeasurementIds[0];
        } else {
            return "Unknown";
        }
    }

    public getMeasurementName(entry: TimelineEntryDTO): string {
        this.ensureHasMeasurementName(entry);
        const measurementId = this.getMeasurementId(entry);
        return this.measurementNameMap[measurementId];
    }

    // Fetches the name of the measurement if it is not already in the list of measurements
    public ensureHasMeasurementName(entry: TimelineEntryDTO): boolean {
        const measurementId = this.getMeasurementId(entry);

        // If the measurement is already in the list of measurements, we fetched or are fetching the name
        if (this.measurementNameMap[measurementId] != undefined) return true;

        // If the measurement is not in the list of measurementIds, we can't fetch the name
        const measurementNotFound = this.measurementRepository.measurementIds.indexOf(measurementId) == -1;
        if (measurementNotFound) {
            this.measurementNameMap[measurementId] = this.measurementNameMap["Unknown"];
            return true;
        }

        // If the measurement is not in the list of measurements, we need to fetch the name
        this.measurementNameMap[measurementId] = "Loading...";
        this.measurementRepository
            .getMeasurement(measurementId)
            .then((m) => {
                this.measurementNameMap[measurementId] = m.name;
            })
            .catch(() => {
                this.measurementNameMap[measurementId] = this.measurementNameMap["Unknown"];
            });
        return true;
    }
    //#endregion

    //#region Private methods
    // Create a query string for the frequency ranges
    private generateFrequencyRangesQueryString(entry: TimelineEntryDTO): string {
        let frequencyRangesQueryString = "";

        if (entry.properties.frequencyRanges && entry.properties.frequencyRanges.length > 0) {
            for (let i = 0; i < entry.properties.frequencyRanges.length; i++) {
                const range = entry.properties.frequencyRanges[i];
                const from = range.from;
                const to = range.to;
                const isSelected = range.isSelected ? 1 : 0;

                // Add safe URI encoding of: &frequencyRanges[i]=from,to,isSelected
                frequencyRangesQueryString +=
                    "&" +
                    fixedEncodeURIComponent(`frequencyRanges[${i}]`) +
                    "=" +
                    fixedEncodeURIComponent(`${from},${to},${isSelected}`);
            }
        }

        return frequencyRangesQueryString;
    }
    //#endregion
}
